import React, { useState, useEffect } from 'react';
import {
  FaCalendarAlt,
  FaSearch,
  FaFilter,
  FaEye,
  FaChevronDown,
  FaChevronUp,
} from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const AQ_PRICING_BASE_URL = process.env.REACT_APP_AQ_PRICING_BASE_URL;

const PricingOverview = () => {
  const [pricingData, setPricingData] = useState([]);
  const [filteredPricingData, setFilteredPricingData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('all');
  const [expandedTemplates, setExpandedTemplates] = useState({});
  const navigate = useNavigate();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [templatesPerPage] = useState(10);

  // Get unique template names for filter
  const templateNames = [...new Set(pricingData.map((item) => item.templatename))];

  // Fetch pricing data based on selected date
  const fetchPricingData = async (targetDate) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${AQ_PRICING_BASE_URL}/pricing/api/get-template-prices/?target_date=${targetDate}`
      );
      setPricingData(response.data.templates);
      setFilteredPricingData(response.data.templates);
      setCurrentPage(1);

      // Initialize expanded state for all templates
      const initialExpandedState = {};
      response.data.templates.forEach((template) => {
        initialExpandedState[template.templatename] = false;
      });
      setExpandedTemplates(initialExpandedState);
    } catch (error) {
      setError('Failed to load pricing data. Please try again.');
      console.error('API Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const dateToFetch = selectedDate.toISOString().split('T')[0];
    fetchPricingData(dateToFetch);
  }, [selectedDate]);

  // Filter templates based on search and selection
  useEffect(() => {
    let filtered = pricingData;

    if (selectedTemplate !== 'all') {
      filtered = filtered.filter((template) => template.templatename === selectedTemplate);
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (template) =>
          template.templatename.toLowerCase().includes(searchTerm.toLowerCase()) ||
          template.active_factors.some((factor) =>
            factor.toLowerCase().includes(searchTerm.toLowerCase())
          ) ||
          Object.values(template.prices).some((price) =>
            price.roomtypename.toLowerCase().includes(searchTerm.toLowerCase())
          )
      );
    }

    setFilteredPricingData(filtered);
    setCurrentPage(1);
  }, [searchTerm, selectedTemplate, pricingData]);

  // Pagination logic
  const indexOfLastTemplate = currentPage * templatesPerPage;
  const indexOfFirstTemplate = indexOfLastTemplate - templatesPerPage;
  const currentTemplates = filteredPricingData.slice(indexOfFirstTemplate, indexOfLastTemplate);
  const totalPages = Math.ceil(filteredPricingData.length / templatesPerPage);

  // Pagination functions
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const getVisiblePages = () => {
    const totalPages = Math.ceil(filteredPricingData.length / templatesPerPage);
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + 4);

    if (endPage - startPage < 4) {
      startPage = Math.max(1, endPage - 4);
    }

    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setShowDatePicker(false);
  };

  const handleCalendarClick = () => {
    setShowDatePicker(!showDatePicker);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleTemplateFilterChange = (e) => {
    setSelectedTemplate(e.target.value);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedTemplate('all');
  };

  const toggleTemplateExpansion = (templateName) => {
    setExpandedTemplates((prev) => ({
      ...prev,
      [templateName]: !prev[templateName],
    }));
  };

  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Date selection options
  const quickDateOptions = [
    { label: 'Today', value: 0 },
    { label: 'Tomorrow', value: 1 },
    { label: 'Next Week', value: 7 },
    { label: 'Next Month', value: 30 },
  ];

  const handleQuickDateSelect = (daysToAdd) => {
    const newDate = new Date();
    newDate.setDate(newDate.getDate() + daysToAdd);
    setSelectedDate(newDate);
  };

  // Render pricing table for a template
  const renderPricingTable = (template) => {
    const prices = template.prices || {};
    const priceEntries = Object.entries(prices);

    if (priceEntries.length === 0) {
      return (
        <div className="text-center text-gray-500 py-4 italic">
          No pricing data available for this template
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                Room Type
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                Base Price
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                Dynamic Price
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                Price Difference
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {priceEntries.map(([key, price]) => {
              const basePrice = price.baseprice || 0;
              const dynamicPrice = price.dynamicprice || 0;
              const priceDifference = dynamicPrice - basePrice;
              const isIncreased = priceDifference > 0;
              const isDecreased = priceDifference < 0;

              return (
                <tr key={key} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {price.roomtypename}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">₹{basePrice.toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                    ₹{dynamicPrice.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={`font-medium ${
                        isIncreased
                          ? 'text-green-600'
                          : isDecreased
                            ? 'text-red-600'
                            : 'text-gray-600'
                      }`}
                    >
                      {isIncreased ? '+' : ''}
                      {priceDifference.toLocaleString()}
                      {priceDifference !== 0 && (
                        <span className="text-xs ml-1">
                          ({((priceDifference / basePrice) * 100).toFixed(1)}%)
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        isIncreased
                          ? 'bg-green-100 text-green-800'
                          : isDecreased
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {isIncreased ? 'Increased' : isDecreased ? 'Decreased' : 'No Change'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // Render active factors as badges
  const renderActiveFactors = (factors) => {
    if (!factors || factors.length === 0) {
      return <span className="text-gray-400 text-sm italic">No active factors</span>;
    }

    return (
      <div className="flex flex-wrap gap-1">
        {factors.map((factor, index) => (
          <span
            key={index}
            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
          >
            {factor}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen flex flex-col">
      {/* Header - Your exact style */}
      <header className="flex justify-between items-center mb-3 bg-white p-3">
        <h1 className="text-black text-xl font-bold ml-2">Pricing Service</h1>
        <div className="flex space-x-6">
          <button
            onClick={() => navigate('/dashboard/core-modules/revenue-management/pricing-overview')}
            className="btn text-black font-bold "
          >
            Overview
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/revenue-management/pricing-template')}
            className="btn "
          >
            Templates
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/revenue-management/modify-factors')}
            className="btn "
          >
            Factors
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/revenue-management/pricing')}
            className="btn"
          >
            Pricing
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/revenue-management/room-rates')}
            className="btn"
          >
            Room Rates
          </button>
        </div>
      </header>

      <div className="bg-white rounded-lg shadow-sm">
        {/* Page Heading and Description */}
        <div className="bg-white p-6 mb-1 text-center mt-2">
          <h1 className="text-xl font-bold text-gray-800">Pricing Overview</h1>
          <p className="text-gray-600 text-sm">
            Analyze and compare pricing templates by date, including dynamic rates, base prices and
            room type variations.
          </p>
          <hr className="border border-gray-300 w-1/3 mx-auto mb-1 mt-2" />
        </div>

        {/* Date Selection Section */}
        <div className="bg-white p-4 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center space-x-4">
              <span className="font-semibold text-gray-900">Select Date:</span>

              {/* Quick Date Selection */}
              <div className="flex space-x-2">
                {quickDateOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleQuickDateSelect(option.value)}
                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                      selectedDate.toDateString() ===
                      new Date(new Date().getDate() + option.value).toDateString()
                        ? 'bg-black text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              {/* Custom Date Selection */}
              <div className="flex items-center space-x-2">
                <span className="border border-gray-300 px-3 py-1 rounded text-center ">
                  <span className="text-gray-900 font-semibold">{formatDate(selectedDate)}</span>
                </span>
                <div className="relative">
                  <button
                    onClick={handleCalendarClick}
                    className="p-2 bg-black text-white rounded hover:bg-gray-800 transition-colors"
                  >
                    <FaCalendarAlt className="text-sm" />
                  </button>
                  {showDatePicker && (
                    <div className="absolute z-50 mt-1 top-full right-0 bg-white border border-gray-300 rounded shadow-lg">
                      <DatePicker
                        selected={selectedDate}
                        onChange={handleDateChange}
                        dateFormat="yyyy-MM-dd"
                        inline
                        minDate={new Date()}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Results Count */}
            <div className="text-sm text-gray-600 font-medium">
              Showing {indexOfFirstTemplate + 1}-
              {Math.min(indexOfLastTemplate, filteredPricingData.length)} of{' '}
              {filteredPricingData.length} templates
            </div>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white p-4 border-b border-gray-200">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search Input */}
            <div className="flex-1 relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search templates, factors or room types..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded bg-white outline-none"
              />
            </div>

            {/* Template Filter */}
            <div className="flex gap-4">
              <div className="relative min-w-[200px]">
                <FaFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <select
                  value={selectedTemplate}
                  onChange={handleTemplateFilterChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded appearance-none bg-white"
                >
                  <option value="all">All Templates</option>
                  {templateNames.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Clear Filters Button */}
              {(searchTerm || selectedTemplate !== 'all') && (
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors font-medium"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="p-6">
          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-white border border-gray-300 rounded p-6 text-center">
              <p className="text-gray-800 text-lg font-semibold">{error}</p>
              <button
                onClick={() => fetchPricingData(selectedDate.toISOString().split('T')[0])}
                className="mt-4 px-6 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors"
              >
                Retry
              </button>
            </div>
          )}

          {/* Pricing Data Table */}
          {!loading && !error && (
            <div className="space-y-4">
              {currentTemplates.length === 0 ? (
                <div className="text-center text-gray-600 py-12">
                  <p className="text-xl font-semibold">No templates match your search criteria.</p>
                  <button
                    onClick={clearFilters}
                    className="mt-4 px-6 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors"
                  >
                    Clear Filters
                  </button>
                </div>
              ) : (
                currentTemplates.map((template) => (
                  <div
                    key={template.templatename}
                    className="bg-white border border-gray-200 rounded-lg overflow-hidden"
                  >
                    {/* Template Header */}
                    <div
                      className="flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                      onClick={() => toggleTemplateExpansion(template.templatename)}
                    >
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          {expandedTemplates[template.templatename] ? (
                            <FaChevronUp className="text-gray-500" />
                          ) : (
                            <FaChevronDown className="text-gray-500" />
                          )}
                          <h3 className="text-lg font-semibold text-gray-800">
                            {template.templatename}
                          </h3>
                        </div>
                        {/* <div className="text-sm text-gray-500">
                          {Object.keys(template.prices || {}).length} room types
                        </div> */}
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <div className="text-sm font-medium text-gray-600">Active Factors</div>
                          {renderActiveFactors(template.active_factors)}
                        </div>
                        <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                          <FaEye />
                        </button>
                      </div>
                    </div>

                    {/* Expandable Content */}
                    {expandedTemplates[template.templatename] && (
                      <div className="border-t border-gray-200">{renderPricingTable(template)}</div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Pagination */}
          {!loading && !error && filteredPricingData.length > 0 && (
            <div className="flex justify-center items-center mt-8 space-x-2">
              {/* Previous Button */}
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-black text-white rounded disabled:bg-gray-400 disabled:cursor-not-allowed hover:bg-gray-800 transition-colors"
              >
                Previous
              </button>

              {/* Page Numbers */}
              {getVisiblePages().map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-4 py-2 rounded transition-colors ${
                    currentPage === page
                      ? 'bg-black text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {page}
                </button>
              ))}

              {/* Next Button */}
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-black text-white rounded disabled:bg-gray-400 disabled:cursor-not-allowed hover:bg-gray-800 transition-colors"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PricingOverview;
